import React, { useState, useEffect, useRef } from 'react';
import { Bot, Send, DollarSign, X, User, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useWallet } from '../contexts/WalletContext';
import FirebaseAuth from './FirebaseAuth';
import ReactMarkdown from 'react-markdown';
import './PublicChatInterface.css';
import './ReactAdminPrototype.css';

// Preprocess markdown content to convert [ ... ] to \[ ... \] for block math equations
// OpenAI sometimes outputs equations in [ ... ] format, we convert to \[ ... \] which MathJax natively supports
const preprocessMathContent = (content) => {
  if (!content || typeof content !== 'string') return content;
  
  // Convert [ ... ] patterns that contain LaTeX to \[ ... \] format (MathJax block math)
  // Match [ followed by content until ], but only if content contains LaTeX commands
  return content.replace(/\[\s*([^\]]+)\s*\]/g, (match, equation) => {
    const trimmed = equation.trim();
    // Check if this contains LaTeX commands (backslash followed by letters like \text, \frac, etc.)
    if (/\\[a-zA-Z]/.test(trimmed)) {
      // Convert to \[ ... \] format (MathJax's native block math delimiter)
      return `\\[${trimmed}\\]`;
    }
    return match;
  });
};

// Fix malformed markdown links in text before rendering
// Handles cases where URLs contain markdown syntax like ]( or %5D(
const fixMalformedMarkdownLinks = (text) => {
  if (!text || typeof text !== 'string') return text;
  
  let fixed = text;
  
  // Pattern 1: Handle [text](url](url)) or [text](url%5D(url)) with extra closing parens
  fixed = fixed.replace(/\[([^\]]*?)\]\((https?:\/\/[^\)]*?)(?:%5D\(|]\([^\)]*?\)+)\)+/g, (match, linkText, url) => {
    const cleanUrl = url.split(/%5D\(|]\(/)[0].trim();
    const cleanLinkText = linkText.replace(/^\[+/, '').trim();
    return `[${cleanLinkText}](${cleanUrl})`;
  });
  
  // Pattern 2: Handle cases with extra leading brackets: [[text](url](url))
  fixed = fixed.replace(/\[+([^\]]*?)\]\((https?:\/\/[^\)]*?)(?:%5D\(|]\([^\)]*?\)+)\)+/g, (match, linkText, url) => {
    const cleanUrl = url.split(/%5D\(|]\(/)[0].trim();
    const cleanLinkText = linkText.replace(/^\[+/, '').trim();
    return `[${cleanLinkText}](${cleanUrl})`;
  });
  
  // Pattern 3: Clean up any remaining stray brackets/parens around links
  // Remove leading [ that appears right before a markdown link pattern
  fixed = fixed.replace(/\[(\[([^\]]+)\]\(https?:\/\/[^\)]+\))/g, '$1');
  // Remove trailing ) that appears right after a markdown link's closing paren
  // Match: [text](url)) -> [text](url)
  fixed = fixed.replace(/(\[([^\]]+)\]\(https?:\/\/[^\)]+\))\)+/g, (match) => {
    // Only keep one closing paren (the one that closes the markdown link)
    const firstClose = match.indexOf(')');
    return match.substring(0, firstClose + 1);
  });
  
  return fixed;
};

// Fix malformed hrefs that contain markdown syntax (e.g., "url%5D(url)" -> "url")
const fixMalformedHref = (href) => {
  if (!href || typeof href !== 'string') return href;
  // Check if href contains the pattern %5D( which is URL-encoded ](
  const malformedPattern = /%5D\(/;
  if (malformedPattern.test(href)) {
    // Extract just the URL part before the malformed markdown syntax
    return href.split(malformedPattern)[0];
  }
  // Also check for literal ]( pattern (in case it's not URL-encoded)
  const literalPattern = /\]\(/;
  if (literalPattern.test(href)) {
    return href.split(literalPattern)[0];
  }
  return href;
};

const PublicChatInterface = ({ agent, onBack, conversationId: initialConversationId, onUpdateUrl }) => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState(initialConversationId);
  
  // Sync conversationId state with prop changes
  useEffect(() => {
    console.log('🔄 [PROP_SYNC] ========== PROP SYNC EFFECT ==========');
    console.log('🔄 [PROP_SYNC] initialConversationId prop:', initialConversationId);
    console.log('🔄 [PROP_SYNC] conversationId state:', conversationId);
    console.log('🔄 [PROP_SYNC] Are they different?', initialConversationId !== conversationId);
    
    if (initialConversationId !== conversationId) {
      console.log('🔄 [PROP_SYNC] Prop changed! Syncing state');
      console.log('🔄 [PROP_SYNC] Setting conversationId from', conversationId, 'to', initialConversationId);
      console.log('🔄 [PROP_SYNC] This will trigger the conversationId useEffect');
      setConversationId(initialConversationId);
      console.log('🔄 [PROP_SYNC] setConversationId called');
    } else {
      console.log('🔄 [PROP_SYNC] Props match state, no sync needed');
    }
    console.log('🔄 [PROP_SYNC] ========== PROP SYNC COMPLETE ==========');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialConversationId]);
  const [conversation, setConversation] = useState(null);
  const [agentAvatar, setAgentAvatar] = useState(null);
  const [userAgentAvatar, setUserAgentAvatar] = useState(null);
  const [agentPricing, setAgentPricing] = useState(null);
  const [isPaidAgent, setIsPaidAgent] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [conversationState, setConversationState] = useState(null);
  const [isRequestingEngagement, setIsRequestingEngagement] = useState(false);
  const [nextMessageCost, setNextMessageCost] = useState(0);
  const [canSendFreeMessage, setCanSendFreeMessage] = useState(true);
  const [freeMessageStatus, setFreeMessageStatus] = useState(null);
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [guestMessageCount, setGuestMessageCount] = useState(0);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [authMode, setAuthMode] = useState('signup');
  const [isDeleting, setIsDeleting] = useState(false);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const navigate = useNavigate();
  const { getToken, isAuthenticated, user } = useAuth();
  const { balance: walletBalance, topUpWallet, refreshWalletBalance } = useWallet();

  // Guest message limit constant
  const GUEST_MESSAGE_LIMIT = 2;

  // Get agent avatar and pricing (for the agent being chatted with)
  useEffect(() => {
    const fetchAgentData = async () => {
      if (agent?.id) {
        try {
          const response = await fetch(`/api/agents/public`);
          if (response.ok) {
            const data = await response.json();
            const agentData = data.agents?.find(a => a.id === agent.id);
            console.log('Agent being chatted with:', agentData);
            if (agentData?.agent_avatar) {
              console.log('Setting agent avatar (B):', agentData.agent_avatar);
              setAgentAvatar(agentData.agent_avatar);
            }
            
            // Set pricing information
            if (agentData?.owner_engagement_fee_cents) {
              setAgentPricing({
                owner_engagement_fee_cents: agentData.owner_engagement_fee_cents,
                is_public: agentData.is_public
              });
              setIsPaidAgent(agentData.owner_engagement_fee_cents > 0);
            }
          }
        } catch (error) {
          console.error('Error fetching agent data:', error);
        }
      }
    };
    fetchAgentData();
  }, [agent?.id]);

  // Get user's agent avatar (for the user sending messages)
  useEffect(() => {
    const fetchUserAgentAvatar = async () => {
      const token = await getToken();
      if (token) {
        try {
          // Get user's agent data using the existing settings endpoint
          const response = await fetch('/api/agents/settings', {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          console.log('User agent settings response status:', response.status);
          if (response.ok) {
            const data = await response.json();
            console.log('User agent settings data:', data);
            
            if (data.id) {
              // Now get the user's agent from the public agents list using the agent ID
              const publicResponse = await fetch('/api/agents/public');
              if (publicResponse.ok) {
                const publicData = await publicResponse.json();
                const userAgentData = publicData.agents?.find(a => a.id === data.id);
                console.log('User agent from public list:', userAgentData);
                
                if (userAgentData?.agent_avatar) {
                  console.log('Setting user agent avatar (A):', userAgentData.agent_avatar);
                  setUserAgentAvatar(userAgentData.agent_avatar);
                } else {
                  console.log('No agent_avatar found for user agent in public list');
                }
              } else {
                console.log('Failed to fetch public agents list');
              }
            } else {
              console.log('No agent ID in user settings');
            }
          } else {
            console.log('Failed to fetch user agent settings, status:', response.status);
          }
        } catch (error) {
          console.error('Error fetching user agent avatar:', error);
        }
      } else {
        console.log('No token available for fetching user agent avatar');
      }
    };
    fetchUserAgentAvatar();
  }, [getToken, isAuthenticated]);

  // Load conversation state when conversationId changes
  useEffect(() => {
    const loadConversationState = async () => {
      if (conversationId && isAuthenticated) {
        try {
          const token = await getToken();
          if (!token) return;
          
          console.log(`🔍 [CHAT_INTERFACE] Loading conversation state for conversation ${conversationId}`);
          const response = await fetch(`/api/agents/public/conversations/${conversationId}`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          if (response.ok) {
            const conversationData = await response.json();
            console.log(`✅ [CHAT_INTERFACE] Loaded conversation state:`, conversationData);
            setConversationState(conversationData.conversation);
          }
        } catch (error) {
          console.error('Error loading conversation state:', error);
        }
      }
    };
    loadConversationState();
  }, [conversationId, agent.id, isAuthenticated, getToken]);

  // Prevent body scrolling when chat is active
  useEffect(() => {
    document.body.classList.add('chat-active');
    return () => {
      document.body.classList.remove('chat-active');
    };
  }, []);

  // Diagnostic: Log when typing indicator should be visible
  useEffect(() => {
    if (isLoading) {
      console.log('🎯 [TYPING_INDICATOR] Typing indicator should be visible - isLoading is true');
      console.log('🎯 [TYPING_INDICATOR] Check browser console and DOM for .typing-indicator element');
    }
  }, [isLoading]);

  // Load conversation messages if conversationId is provided
  const loadConversationMessages = async (convId) => {
    console.log('📥 [LOAD_MESSAGES] ========== LOADING MESSAGES ==========');
    console.log('📥 [LOAD_MESSAGES] Conversation ID:', convId);
    console.log('📥 [LOAD_MESSAGES] Current messages before load:', messages.length);
    console.log('📥 [LOAD_MESSAGES] Current messages:', messages.map(m => ({ id: m.id, type: m.type })));
    
    try {
      console.log('📡 [LOAD_MESSAGES] Fetching from API: /api/agents/public/conversations/' + convId);
      const response = await fetch(`/api/agents/public/conversations/${convId}`);
      console.log('📡 [LOAD_MESSAGES] API response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('📡 [LOAD_MESSAGES] API response data received');
        const conversationData = data.conversation;
        
        if (conversationData && conversationData.messages) {
          console.log('📥 [LOAD_MESSAGES] Found', conversationData.messages.length, 'messages from server');
          console.log('📥 [LOAD_MESSAGES] Raw messages:', conversationData.messages.map(m => ({ id: m.id, role: m.role, content: m.content?.substring(0, 50) })));
          
          setConversation(conversationData);
          
          const formattedMessages = conversationData.messages.map(msg => {
            const formatted = {
              id: msg.id,
              type: msg.role === 'user' ? 'user' : 'assistant',
              content: msg.content,
              timestamp: new Date(msg.timestamp),
              isOwnerResponse: msg.is_owner_response || false
            };
            return formatted;
          });
          
          console.log('📥 [LOAD_MESSAGES] Formatted', formattedMessages.length, 'messages');
          console.log('📥 [LOAD_MESSAGES] About to call setMessages - this will REPLACE current messages');
          console.log('📥 [LOAD_MESSAGES] Current messages will be lost:', messages.length);
          setMessages(formattedMessages);
          console.log('📥 [LOAD_MESSAGES] setMessages called - messages replaced');
          console.log('📥 [LOAD_MESSAGES] New messages count:', formattedMessages.length);
        } else {
          console.log('⚠️ [LOAD_MESSAGES] No messages found in conversation data');
        }
      } else {
        console.log('❌ [LOAD_MESSAGES] Failed to fetch conversation, status:', response.status);
      }
    } catch (error) {
      console.error('❌ [LOAD_MESSAGES] Error loading conversation messages:', error);
    }
    console.log('📥 [LOAD_MESSAGES] ========== LOADING COMPLETE ==========');
  };

  // Track if we just set conversationId from a response (to avoid unnecessary reload)
  const justSetConversationIdRef = useRef(false);

  // Load conversation when conversationId changes
  useEffect(() => {
    console.log('🔄 [CONVERSATION_ID_EFFECT] ========== EFFECT TRIGGERED ==========');
    console.log('🔄 [CONVERSATION_ID_EFFECT] conversationId:', conversationId);
    console.log('🔄 [CONVERSATION_ID_EFFECT] justSetConversationIdRef.current:', justSetConversationIdRef.current);
    console.log('🔄 [CONVERSATION_ID_EFFECT] Current messages count:', messages.length);
    console.log('🔄 [CONVERSATION_ID_EFFECT] Current messages:', messages.map(m => ({ id: m.id, type: m.type, content: m.content?.substring(0, 50) })));
    
    if (conversationId) {
      // If we just set conversationId from a response, skip reload (messages are already in state)
      if (justSetConversationIdRef.current) {
        console.log('✅ [CONVERSATION_ID_EFFECT] Flag is TRUE - skipping reload to preserve messages');
        console.log('✅ [CONVERSATION_ID_EFFECT] Messages will remain:', messages.length);
        justSetConversationIdRef.current = false; // Reset flag
        console.log('✅ [CONVERSATION_ID_EFFECT] Flag reset to false');
        console.log('🔄 [CONVERSATION_ID_EFFECT] ========== EFFECT COMPLETE (SKIPPED) ==========');
        return;
      }
      console.log('📡 [CONVERSATION_ID_EFFECT] Flag is FALSE - will reload messages from server');
      console.log('📡 [CONVERSATION_ID_EFFECT] Calling loadConversationMessages for:', conversationId);
      loadConversationMessages(conversationId);
    } else {
      console.log('⚠️ [CONVERSATION_ID_EFFECT] No conversationId, clearing messages');
      setMessages([]);
    }
    console.log('🔄 [CONVERSATION_ID_EFFECT] ========== EFFECT COMPLETE ==========');
  }, [conversationId]);

  // Initialize messages when agent changes (but only if we don't have messages already)
  useEffect(() => {
    console.log('🔄 [AGENT_EFFECT] Agent changed:', { agent: agent?.id, conversationId, currentMessagesCount: messages.length });
    if (agent && !conversationId && messages.length === 0) {
      // Start with empty chat - no intro message (only if we don't have messages)
      console.log('🔄 [AGENT_EFFECT] Starting with empty chat for new agent');
      setMessages([]);
    } else if (agent && conversationId && messages.length > 0) {
      console.log('🔄 [AGENT_EFFECT] Agent changed but we have messages and conversationId - preserving messages');
    }
  }, [agent]);

  // Reset guest message count when user becomes authenticated or conversation resets
  useEffect(() => {
    if (isAuthenticated) {
      // User is authenticated, guest count is irrelevant
      setGuestMessageCount(0);
    } else if (!conversationId) {
      // New conversation for guest user, reset count
      setGuestMessageCount(0);
    }
  }, [isAuthenticated, conversationId]);

  // Close login modal when user becomes authenticated
  useEffect(() => {
    if (isAuthenticated && showLoginModal) {
      console.log('User authenticated, closing login modal');
      setShowLoginModal(false);
      // Refresh the page to pick up the authenticated state and update conversation
      setTimeout(() => {
        window.location.reload();
      }, 500);
    }
  }, [isAuthenticated, showLoginModal]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Render MathJax equations after messages update
  useEffect(() => {
    if (typeof window !== 'undefined' && window.MathJax && window.MathJax.typesetPromise) {
      window.MathJax.typesetPromise().catch((err) => {
        console.error('MathJax typeset error:', err);
      });
    }
  }, [messages]);

  // Update message cost and free message status when agent or auth changes
  useEffect(() => {
    const updateMessageInfo = async () => {
      if (isPaidAgent && isAuthenticated) {
        try {
          const token = await getToken();
          if (!token) return;
          
          // Get free message status from server
          const response = await fetch(`/api/wallet/free-message-status/${agent.id}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (response.ok) {
            const status = await response.json();
            setFreeMessageStatus(status);
            console.log('🆓 [UI_UPDATE] Free message status:', status);
          }
          
          // Check if user can send message (free or paid)
          const canSend = await canSendMessage();
          setCanSendFreeMessage(canSend);
          
          // Always get the message cost, regardless of input
          const cost = await getNextMessageCost();
          setNextMessageCost(cost);
        } catch (error) {
          console.error('Error updating message info:', error);
          setNextMessageCost(0);
          setCanSendFreeMessage(false);
        }
      } else {
        setNextMessageCost(0);
        setCanSendFreeMessage(true);
        setFreeMessageStatus(null);
      }
    };

    updateMessageInfo();
  }, [isPaidAgent, isAuthenticated, walletBalance, agent.id]); // Removed inputMessage dependency

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [inputMessage]);

  // Check if user can send a message (free or paid)
  const canSendMessage = async () => {
    if (!isPaidAgent) {
      console.log('✅ [MESSAGE_CHECK] Free agent - allowing message');
      return true; // Free agent
    }
    if (!isAuthenticated) {
      console.log('❌ [MESSAGE_CHECK] Not authenticated - blocking message');
      return false; // Must be authenticated for paid agents
    }
    
    // Use cached free message status if available
    if (freeMessageStatus) {
      console.log('🆓 [MESSAGE_CHECK] Using cached free message status:', freeMessageStatus);
      
      // Can send if they have free messages OR sufficient funds for paid message
      if (freeMessageStatus.can_send_free || freeMessageStatus.free_messages_remaining > 0) {
        console.log('✅ [FREE_CHECK] User can send free message');
        return true;
      }
      
      console.log('💰 [PAID_CHECK] No free messages, checking if user can afford paid message');
      
      // Check if they have sufficient funds for a paid message
      const messageCost = nextMessageCost || 1; // Use cached cost or default to 1 cent
      console.log('💰 [PAID_CHECK] Message cost:', messageCost, 'Wallet balance:', walletBalance);
      const canAfford = walletBalance >= messageCost;
      console.log('✅ [PAID_CHECK] User can afford paid message:', canAfford);
      return canAfford;
    }
    
    console.log('❌ [MESSAGE_CHECK] No cached status available, defaulting to false');
    return false; // Default to false if no cached status
  };

  // Count user messages in current conversation
  const getUserMessageCount = () => {
    return messages.filter(msg => msg.type === 'user').length;
  };

  // Check if guest user can send message (future-ready structure)
  const canSendGuestMessage = () => {
    if (isAuthenticated) return true;
    return guestMessageCount < GUEST_MESSAGE_LIMIT;
  };

  // Get the cost for the next message
  const getNextMessageCost = async () => {
    if (!isPaidAgent) return 0;
    if (!isAuthenticated) return 0;
    
    // Return cached cost if available
    if (nextMessageCost > 0) {
      console.log('💰 [COST_CALC] Using cached message cost:', nextMessageCost);
      return nextMessageCost;
    }
    
    try {
      const token = await getToken();
      if (!token) return 1;
      
      const response = await fetch(`/api/wallet/message-cost/${agent.id}`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ message: inputMessage || 'test' })
      });
      if (response.ok) {
        const cost = await response.json();
        console.log('💰 [COST_CALC] Message cost calculated:', cost);
        return cost.cost_cents;
      } else {
        console.error('❌ [COST_CALC] Failed to calculate cost:', response.status);
      }
    } catch (error) {
      console.error('❌ [COST_CALC] Error calculating message cost:', error);
    }
    
    return 1; // Default to 1 cent for paid agents
  };

  // Handle owner engagement request
  const handleRequestOwnerEngagement = async () => {
    if (!conversationId || !agentPricing?.owner_engagement_fee_cents || isRequestingEngagement) {
      return;
    }

    setIsRequestingEngagement(true);
    try {
      const token = await getToken();
      if (!token) {
        setIsRequestingEngagement(false);
        return;
      }
      
      console.log(`🔍 [CHAT_INTERFACE] Requesting owner engagement for conversation ${conversationId}`);
      const response = await fetch('/api/wallet/request-owner-engagement', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          conversationId: conversationId,
          agentId: agent.id
        })
      });

      if (response.ok) {
        const result = await response.json();
        console.log(`✅ [CHAT_INTERFACE] Owner engagement requested successfully:`, result);
        
        // Update conversation state
        setConversationState(prev => ({
          ...prev,
          owner_response_state: 'requested',
          engagement_fee_cents: result.engagement_fee_cents,
          requested_at: result.requested_at
        }));
        
        // Refresh wallet balance
        if (window.refreshWalletBalance) {
          window.refreshWalletBalance();
        }
      } else {
        const errorData = await response.json();
        console.error(`❌ [CHAT_INTERFACE] Failed to request owner engagement:`, errorData);
        alert(`Failed to request owner engagement: ${errorData.error}`);
      }
    } catch (error) {
      console.error('Error requesting owner engagement:', error);
      alert('Error requesting owner engagement. Please try again.');
    } finally {
      setIsRequestingEngagement(false);
    }
  };

  // Handle rescinding owner engagement request
  const handleRescindOwnerEngagement = async () => {
    if (!conversationId || isRequestingEngagement) {
      return;
    }

    setIsRequestingEngagement(true);
    try {
      const token = await getToken();
      if (!token) {
        setIsRequestingEngagement(false);
        return;
      }
      
      console.log(`🔍 [CHAT_INTERFACE] Rescinding owner engagement for conversation ${conversationId}`);
      const response = await fetch('/api/wallet/rescind-owner-engagement', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          conversationId: conversationId,
          agentId: agent.id
        })
      });

      if (response.ok) {
        const result = await response.json();
        console.log(`✅ [CHAT_INTERFACE] Owner engagement rescinded successfully:`, result);
        
        // Update conversation state back to not_requested
        setConversationState(prev => ({
          ...prev,
          owner_response_state: 'not_requested',
          engagement_fee_cents: 0,
          requested_at: null
        }));
        
        // Refresh wallet balance (refund should be processed)
        if (window.refreshWalletBalance) {
          window.refreshWalletBalance();
        }
      } else {
        const errorData = await response.json();
        console.error(`❌ [CHAT_INTERFACE] Failed to rescind owner engagement:`, errorData);
        alert(`Failed to rescind owner engagement: ${errorData.error}`);
      }
    } catch (error) {
      console.error('Error rescinding owner engagement:', error);
      alert('Error rescinding owner engagement. Please try again.');
    } finally {
      setIsRequestingEngagement(false);
    }
  };

  // Check if owner engagement button should be enabled
  const canRequestOwnerEngagement = () => {
    if (!isAuthenticated || !conversationId || !agentPricing?.owner_engagement_fee_cents) {
      return false;
    }
    
    // Check if already fulfilled (can't request or rescind)
    if (conversationState?.owner_response_state === 'fulfilled') {
      return false;
    }
    
    // If already requested, allow rescinding (button enabled)
    if (conversationState?.owner_response_state === 'requested') {
      return true;
    }
    
    // Check if user has sufficient funds for requesting
    return walletBalance >= agentPricing.owner_engagement_fee_cents;
  };

  // Get button text based on state
  const getOwnerEngagementButtonText = () => {
    if (!conversationState) {
      return `Request Owner Review ($${(agentPricing?.owner_engagement_fee_cents / 100).toFixed(2)})`;
    }
    
    switch (conversationState.owner_response_state) {
      case 'requested':
        return 'Rescind Request';
      case 'fulfilled':
        return 'Owner Review Completed';
      default:
        return `Request Owner Review ($${(agentPricing?.owner_engagement_fee_cents / 100).toFixed(2)})`;
    }
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading || isSendingMessage) return;

    // Check guest message limit for non-authenticated users
    if (!isAuthenticated && !canSendGuestMessage()) {
      setShowLoginModal(true);
      return;
    }

    // Check if user can send message (free or paid)
    const canSend = await canSendMessage();
    if (!canSend) {
      setShowPaymentModal(true);
      return;
    }

    setIsSendingMessage(true);

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: inputMessage.trim(),
      timestamp: new Date()
    };

    console.log('💬 [ADD_USER_MSG] Creating user message:', { id: userMessage.id, type: userMessage.type, contentLength: userMessage.content?.length });
    console.log('💬 [ADD_USER_MSG] Current messages before adding:', messages.length);
    setMessages(prev => {
      const newMessages = [...prev, userMessage];
      console.log('💬 [ADD_USER_MSG] setMessages callback - prev count:', prev.length, 'new count:', newMessages.length);
      console.log('💬 [ADD_USER_MSG] New messages:', newMessages.map(m => ({ id: m.id, type: m.type })));
      return newMessages;
    });
    console.log('💬 [ADD_USER_MSG] setMessages called - user message added');
    
    // Increment guest message count for non-authenticated users
    if (!isAuthenticated) {
      setGuestMessageCount(prev => prev + 1);
    }
    
    setInputMessage('');
    setIsLoading(true);

    try {
      // Call the public agent chat API
        const token = await getToken();
        console.log('Token for API call:', token ? 'present' : 'missing');
        const headers = {
          'Content-Type': 'application/json'
        };
        
        // Add Authorization header if token exists
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
          console.log('Added Authorization header');
        } else {
          console.log('No token, proceeding as anonymous user');
        }

        // Use userMessage.content instead of inputMessage since inputMessage was cleared
        const messageContent = userMessage.content;
        
        const response = await fetch('/api/agents/public/chat', {
          method: 'POST',
          headers,
          body: JSON.stringify({
            agentId: agent.id,
            message: messageContent,
            conversationId: conversationId,
            conversationHistory: messages.slice(-10).map(m => ({
              role: m.type === 'user' ? 'user' : 'assistant',
              content: m.content
            }))
          })
        });

      if (response.ok) {
        const data = await response.json();
        console.log('API response:', data);
        
        // Handle insufficient funds error
        if (data.error === 'Insufficient funds') {
          const errorMessage = {
            id: Date.now() + 1,
            type: 'error',
            content: data.message || 'Insufficient funds. Please add credits to your wallet.',
            timestamp: new Date()
          };
          setMessages(prev => [...prev, errorMessage]);
          setShowPaymentModal(true);
          return;
        }
        
        // After first response, conversation is created - add chat param to URL (keep agent param)
        if (data.conversationId) {
          if (!conversationId) {
            console.log('🎯 [FIRST_RESPONSE] ========== FIRST RESPONSE HANDLING ==========');
            console.log('🎯 [FIRST_RESPONSE] Conversation created:', data.conversationId);
            console.log('🎯 [FIRST_RESPONSE] Current conversationId state:', conversationId);
            console.log('🎯 [FIRST_RESPONSE] Current messages count:', messages.length);
            console.log('🎯 [FIRST_RESPONSE] Current messages:', messages.map(m => ({ id: m.id, type: m.type })));
            console.log('🎯 [FIRST_RESPONSE] Setting justSetConversationIdRef.current = TRUE');
            justSetConversationIdRef.current = true;
            console.log('🎯 [FIRST_RESPONSE] Flag set to:', justSetConversationIdRef.current);
            console.log('🎯 [FIRST_RESPONSE] About to call setConversationId:', data.conversationId);
            setConversationId(data.conversationId);
            console.log('🎯 [FIRST_RESPONSE] setConversationId called - this will trigger useEffect');
            console.log('🎯 [FIRST_RESPONSE] Dispatch conversationCreated event');
            // Dispatch event to refresh sidebar
            window.dispatchEvent(new CustomEvent('conversationCreated'));
            console.log('🎯 [FIRST_RESPONSE] ========== FIRST RESPONSE HANDLING COMPLETE ==========');
          } else {
            console.log('🔄 [SUBSEQUENT_RESPONSE] ConversationId already exists:', conversationId);
          }
          
          // Add chat param to URL (don't replace agent param, and put agent first)
          if (onUpdateUrl) {
            // onUpdateUrl receives params object - preserve existing params and put agent first
            const currentParams = new URLSearchParams(window.location.search);
            const params = {};
            // Put agent param first if it exists
            if (currentParams.get('agent')) {
              params.agent = currentParams.get('agent');
            }
            params.chat = data.conversationId.toString();
            onUpdateUrl(params);
            console.log('✅ [CHAT] URL updated via onUpdateUrl callback with chat param');
          } else {
            // Fallback: use navigate but preserve agent param and put it first
            const currentParams = new URLSearchParams(window.location.search);
            const newSearch = new URLSearchParams();
            // Put agent param first if it exists
            if (currentParams.get('agent')) {
              newSearch.set('agent', currentParams.get('agent'));
            }
            newSearch.set('chat', data.conversationId.toString());
            navigate({ search: newSearch.toString() }, { replace: true });
            console.log('✅ [CHAT] URL updated via navigate with chat param');
          }
        }
        
        const agentResponse = {
          id: Date.now() + 1,
          type: 'assistant',
          content: data.response,
          timestamp: new Date()
        };
        console.log('💬 [ADD_RESPONSE] Creating agent response:', { id: agentResponse.id, type: agentResponse.type, contentLength: agentResponse.content?.length });
        console.log('💬 [ADD_RESPONSE] Current messages before adding response:', messages.length);
        console.log('💬 [ADD_RESPONSE] About to add response to messages');
        setMessages(prev => {
          const newMessages = [...prev, agentResponse];
          console.log('💬 [ADD_RESPONSE] setMessages callback - prev count:', prev.length, 'new count:', newMessages.length);
          console.log('💬 [ADD_RESPONSE] New messages:', newMessages.map(m => ({ id: m.id, type: m.type })));
          return newMessages;
        });
        console.log('💬 [ADD_RESPONSE] setMessages called - response added');
        
        // Log message charging info if available
        if (data.messageInfo) {
          console.log('Message charging info:', data.messageInfo);
          if (data.messageInfo.isFreeMessage) {
            console.log('🆓 Message was free');
          } else {
            console.log(`💰 Message cost: $${data.messageInfo.costDollars}`);
          }
        }
        
        // Refresh wallet balance if user is authenticated
        if (isAuthenticated) {
          console.log('💰 Refreshing wallet balance after message send');
          await refreshWalletBalance();
        }
      } else {
        const errorData = await response.json();
        console.error('API error:', errorData);
        throw new Error(errorData.error || 'Failed to get response from agent');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage = {
        id: Date.now() + 1,
        type: 'error',
        content: 'Sorry, I encountered an error. Please try again.',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
      setIsSendingMessage(false);
    }
  };

  const handleDeleteConversation = async () => {
    if (!conversationId || isDeleting) return;
    
    setIsDeleting(true);
    try {
      const token = await getToken();
      if (!token) {
        alert('You must be signed in to delete conversations');
        setIsDeleting(false);
        return;
      }
      
      const response = await fetch(`/api/agents/public/conversations/${conversationId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        // Dispatch event to refresh sidepanel
        window.dispatchEvent(new CustomEvent('conversationDeleted', { 
          detail: { conversationId } 
        }));
        
        // Navigate back to discover page if deleting active chat
        if (onBack) {
          onBack();
        }
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete conversation');
      }
    } catch (error) {
      console.error('Error deleting conversation:', error);
      alert(error.message || 'Failed to delete conversation. Please try again.');
      setIsDeleting(false);
    }
  };

  if (!agent) {
    return (
      <div className="public-chat-interface">
        <div className="no-agent-selected">
          <Bot size={48} />
          <h3>No Agent Selected</h3>
          <p>Choose an agent from the discover page to start chatting</p>
        </div>
      </div>
    );
  }

  // Debug: Log current avatar states
  console.log('Current avatar states:', {
    userAgentAvatar: userAgentAvatar ? `A has avatar: ${userAgentAvatar.substring(0, 50)}...` : 'A no avatar',
    agentAvatar: agentAvatar ? `B has avatar: ${agentAvatar.substring(0, 50)}...` : 'B no avatar'
  });

  return (
    <div className="public-chat-interface">
      <div className="messages-list">
        {/* Character.ai-style Agent Header - scrolls with messages */}
        <div className="agent-header-featured">
          <div className="agent-avatar-large">
            {agentAvatar ? (
              <img src={agentAvatar} alt={agent.name} />
            ) : (
              <Bot size={48} />
            )}
          </div>
          <div className="agent-info-featured">
            <h2 className="agent-name-featured">{agent.name}</h2>
            <p className="agent-description-featured">{agent.description}</p>
            {isPaidAgent && (
              <div className="agent-pricing-info">
                <DollarSign size={16} />
                <span className="price-display">
                  $0.01 per message after 2 free messages
                </span>
                    {freeMessageStatus && freeMessageStatus.free_messages_remaining > 0 && (
                      <span className="free-messages">
                        ({freeMessageStatus.free_messages_remaining} messages remaining (sign in to continue chatting))
                      </span>
                    )}
              </div>
            )}
          </div>
          {conversationId && (
            <button
              className="delete-chat-button"
              onClick={handleDeleteConversation}
              title="Delete chat"
              disabled={isDeleting}
            >
              <Trash2 size={20} />
            </button>
          )}
        </div>
        
        {messages.map((message, index) => {
          const isUser = message.type === 'user';
          const isOwnerResponse = message.isOwnerResponse;
          const isError = message.type === 'error';
          const isLiveDataIntent = typeof message.content === 'string' && message.content.includes('needs_live_data');
          
          console.log(`Message ${index}:`, {
            type: message.type,
            isUser,
            isOwnerResponse,
            isError,
            userAgentAvatar: userAgentAvatar ? 'has avatar' : 'no avatar',
            agentAvatar: agentAvatar ? 'has avatar' : 'no avatar',
            willShowUserAvatar: isUser && userAgentAvatar,
            willShowAgentAvatar: (isOwnerResponse || (!isUser && !isError)) && agentAvatar,
            actualAvatarBeingUsed: isUser ? (userAgentAvatar ? 'USER_AVATAR' : 'GOOGLE_ICON') : (agentAvatar ? 'AGENT_AVATAR' : 'BOT_ICON')
          });
          
          return (
            <div key={index} className={`message ${isUser ? 'user' : isOwnerResponse ? 'owner-response' : isError ? 'error' : 'assistant'}`}>
              <div className="message-avatar">
                {isUser ? (
                  userAgentAvatar ? (
                    <img src={userAgentAvatar} alt="User Avatar" />
                  ) : (
                    <User size={16} />
                  )
                ) : isOwnerResponse ? (
                  agentAvatar ? (
                    <img src={agentAvatar} alt={agent.name} />
                  ) : (
                    <Bot size={16} />
                  )
                ) : isError ? (
                  <Bot size={16} />
                ) : (
                  <Bot size={16} />
                )}
              </div>
              <div className="message-text" style={isLiveDataIntent ? { color: 'red' } : undefined}>
                {isOwnerResponse && (
                  <div className="owner-response-indicator">Agent Owner</div>
                )}
                {isError && (
                  <div className="error-indicator">Error</div>
                )}
                {!isUser && !isError ? (
                  <div className="markdown-content">
                    <ReactMarkdown
                      components={{
                        a: ({ node, ...props }) => {
                          const fixedHref = fixMalformedHref(props.href);
                          return <a {...props} href={fixedHref} target="_blank" rel="noopener noreferrer" />;
                        }
                      }}
                    >
                      {fixMalformedMarkdownLinks(preprocessMathContent(message.content))}
                    </ReactMarkdown>
                  </div>
                ) : (
                  message.content
                )}
              </div>
            </div>
          );
        })}
        
        {isLoading && (
          <div className="typing-indicator">
            <span style={{ background: '#0066FF', backgroundColor: '#0066FF' }}></span>
            <span style={{ background: '#0066FF', backgroundColor: '#0066FF' }}></span>
            <span style={{ background: '#0066FF', backgroundColor: '#0066FF' }}></span>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>
      <div className="input-area">
        {isPaidAgent && (
          <div className="pricing-info">
            <div className="wallet-balance">
              <span>Balance: ${(walletBalance / 100).toFixed(2)}</span>
            </div>
            {agentPricing?.owner_engagement_fee_cents > 0 && (
              <button 
                className={`owner-engagement-button ${!canRequestOwnerEngagement() ? 'disabled' : ''}`}
                onClick={conversationState?.owner_response_state === 'requested' ? handleRescindOwnerEngagement : handleRequestOwnerEngagement}
                disabled={!canRequestOwnerEngagement() || isRequestingEngagement}
              >
                {isRequestingEngagement ? (conversationState?.owner_response_state === 'requested' ? 'Rescinding...' : 'Requesting...') : getOwnerEngagementButtonText()}
              </button>
            )}
          </div>
        )}
        {!isAuthenticated && (
          <div style={{ 
            fontSize: '0.85rem', 
            color: '#0066FF', 
            marginBottom: '0.25rem',
            textAlign: 'center',
            paddingTop: '0.5rem'
          }}>
            {GUEST_MESSAGE_LIMIT - guestMessageCount > 0 
              ? `${GUEST_MESSAGE_LIMIT - guestMessageCount} message${GUEST_MESSAGE_LIMIT - guestMessageCount > 1 ? 's' : ''} remaining (sign in to continue chatting)`
              : 'Please sign in to continue chatting'
            }
          </div>
        )}
        <div className="input-container">
          <textarea
            ref={textareaRef}
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask anything..."
            className="message-input"
            rows={1}
          />
          <button
            onClick={handleSendMessage}
            disabled={!inputMessage.trim() || isLoading || isSendingMessage || (!isAuthenticated && !canSendGuestMessage())}
            className="send-button"
            title={!isAuthenticated && !canSendGuestMessage() ? "Please sign in to continue chatting" : ""}
          >
            <Send size={16} />
          </button>
        </div>
      </div>

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="payment-modal-overlay">
          <div className="payment-modal">
            <h3>Add Credits to Continue</h3>
            <p>This agent charges $0.01 per message after the first 2 free messages.</p>
            <div className="wallet-info">
              <p>Current balance: ${(walletBalance / 100).toFixed(2)}</p>
              <p>Estimated cost: ${(nextMessageCost / 100).toFixed(2)} per message</p>
            </div>
            <div className="modal-actions">
              
              <button 
                className="cancel-button"
                onClick={() => setShowPaymentModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Login Modal for Guest Users */}
      {showLoginModal && (
        <div className="modal-overlay" onClick={() => setShowLoginModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button 
              className="modal-close"
              onClick={() => setShowLoginModal(false)}
            >
              <X size={24} />
            </button>
            <div style={{ marginBottom: '0.75rem', textAlign: 'center' }}>
              
              <p style={{ color: '#FFFFFF', fontSize: '0.85rem' }}>
                Sign in to continue chatting
              </p>
            </div>
            <div className="auth-mode-toggle">
              <button
                className={authMode === 'signin' ? 'active' : ''}
                onClick={() => setAuthMode('signin')}
              >
                Sign In
              </button>
              <button
                className={authMode === 'signup' ? 'active' : ''}
                onClick={() => setAuthMode('signup')}
              >
                Sign Up
              </button>
            </div>
            <FirebaseAuth 
              mode={authMode}
              onSuccess={() => {
                console.log('Authentication successful, waiting for user sync...');
                // Modal will close automatically when user state is set (via useEffect above)
              }}
              onError={(error) => {
                console.error('Authentication error:', error);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicChatInterface;